import { useEffect } from "react";

export default function useDragScroll(wrapperRef, headerRef) {
  useEffect(() => {
    let slider = wrapperRef.current;
    const header = headerRef.current;

    if (!slider || !header) return;

    // Detect real scrollable element
    let scrollEl = null;
    const all = slider.querySelectorAll("*");

    all.forEach(el => {
      const overflowX = window.getComputedStyle(el).overflowX;
      const scrollWidth = el.scrollWidth;
      const clientWidth = el.clientWidth;

      if (!scrollEl && (overflowX === "auto" || overflowX === "scroll" || scrollWidth > clientWidth + 5)) {
        scrollEl = el;
      }
    });

    if (scrollEl) {
      slider = scrollEl;
    }

    // --------------------------------------------------------------------
    // DRAG CLICK-PREVENTION FLAG
    // --------------------------------------------------------------------
    slider.isDragging = false;
    let dragStartX = 0;

    // --------------------------------------------------------------------
    // DRAG VARIABLES
    // --------------------------------------------------------------------
    let isDown = false;
    let startX;
    let lastX;
    let velocityX = 0;
    let frame;

    const isInHeader = (e) => header.contains(e.target);

    const startDrag = (x) => {
      isDown = true;
      dragStartX = x;
      slider.isDragging = false; // reset drag state on each start

      startX = x - slider.offsetLeft;
      lastX = x;
      slider.style.cursor = "grabbing";
      cancelMomentum();
    };

    const onMouseDown = (e) => {
      if (!isInHeader(e)) return;
      startDrag(e.pageX);
    };

    const onMouseMove = (e) => {
      if (!isDown) return;

      if (Math.abs(e.pageX - dragStartX) > 5) {
        slider.isDragging = true;   // mark dragging
      }

      e.preventDefault();

      const x = e.pageX - slider.offsetLeft;
      const walk = x - startX;
      slider.scrollLeft -= walk;

      velocityX = e.pageX - lastX;
      lastX = e.pageX;
      startX = x;
    };

    const onMouseUp = () => {
      isDown = false;
      slider.style.cursor = "grab";
      startMomentum();
      setTimeout(() => { slider.isDragging = false }, 50);
    };

    // TOUCH EVENTS
    const onTouchStart = (e) => {
      if (!isInHeader(e.touches[0])) return;
      startDrag(e.touches[0].pageX);
    };

    const onTouchMove = (e) => {
      if (!isDown) return;

      if (Math.abs(e.touches[0].pageX - dragStartX) > 5) {
        slider.isDragging = true;
      }

      const t = e.touches[0];
      const x = t.pageX - slider.offsetLeft;
      const walk = x - startX;
      slider.scrollLeft -= walk;

      velocityX = t.pageX - lastX;
      lastX = t.pageX;
      startX = x;
    };

    const onTouchEnd = () => {
      isDown = false;
      startMomentum();
      setTimeout(() => { slider.isDragging = false }, 50);
    };

    // MOMENTUM
    const momentum = () => {
      slider.scrollLeft -= velocityX;
      velocityX *= 0.95;

      if (Math.abs(velocityX) > 0.5) frame = requestAnimationFrame(momentum);
    };

    const startMomentum = () => {
      cancelMomentum();
      frame = requestAnimationFrame(momentum);
    };

    const cancelMomentum = () => {
      cancelAnimationFrame(frame);
    };

    // LISTENERS
    slider.addEventListener("mousedown", onMouseDown);
    slider.addEventListener("mousemove", onMouseMove);
    slider.addEventListener("mouseup", onMouseUp);
    slider.addEventListener("mouseleave", onMouseUp);

    slider.addEventListener("touchstart", onTouchStart, { passive: true });
    slider.addEventListener("touchmove", onTouchMove, { passive: false });
    slider.addEventListener("touchend", onTouchEnd);

    return () => {
      slider.removeEventListener("mousedown", onMouseDown);
      slider.removeEventListener("mousemove", onMouseMove);
      slider.removeEventListener("mouseup", onMouseUp);
      slider.removeEventListener("mouseleave", onMouseUp);

      slider.removeEventListener("touchstart", onTouchStart);
      slider.removeEventListener("touchmove", onTouchMove);
      slider.removeEventListener("touchend", onTouchEnd);

      cancelMomentum();
    };
  }, []);
}
